import React, {useState} from 'react';
import Avatar from '@cloudscape-design/chat-components/avatar';
import ChatBubble from '@cloudscape-design/chat-components/chat-bubble';
import {
    Box,
    Button,
    CopyToClipboard,
    ExpandableSection,
    KeyValuePairs,
    Popover,
    SpaceBetween,
    StatusIndicator,
    Textarea,
} from '@cloudscape-design/components';
import type {StatusIndicatorProps} from '@cloudscape-design/components';
import {CodeView} from '@cloudscape-design/code-view';
import * as awsui from '@cloudscape-design/design-tokens/index.js';
import {isToolUIPart, getToolName} from 'ai';
import type {UIMessage, ToolUIPart} from 'ai';
import {Streamdown} from 'streamdown';
import ArtifactPreview from './artifact-preview';
import type {ArtifactPreviewData, ArtifactContext, CustomDataPart} from './cassandra-types';
import './cassandra.css';

const ASK_QUESTION_TOOL = 'askQuestion';

function formatToolDisplayName(tool: ToolUIPart): string {
    const raw = getToolName(tool);
    return raw
        .replace(/^tool-/, '')
        .replace(/Tool$/, '')
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/^./, (c) => c.toUpperCase());
}

function getStatusType(state: ToolUIPart['state']): StatusIndicatorProps.Type {
    switch (state) {
        case 'input-streaming':
        case 'input-available':
            return 'loading';
        case 'output-available':
            return 'success';
        case 'output-error':
        case 'output-denied':
            return 'error';
        case 'approval-requested':
            return 'pending';
        case 'approval-responded':
            return 'loading';
        default:
            return 'info';
    }
}

function getStatusText(tool: ToolUIPart): string {
    const name = formatToolDisplayName(tool);
    switch (tool.state) {
        case 'output-error':
            return `${name} failed`;
        case 'output-denied':
            return `${name} rejected`;
        case 'approval-requested':
            return `${name} needs approval`;
        default:
            return name;
    }
}

function prettifyMaybeJson(content: unknown): string {
    if (typeof content !== 'string') return JSON.stringify(content, null, 2);
    try {
        return JSON.stringify(JSON.parse(content), null, 2);
    } catch {
        return content;
    }
}

function ToolDetail({tool}: {tool: ToolUIPart}) {
    const input = tool.input !== undefined ? prettifyMaybeJson(tool.input) : 'Loading...';

    if (tool.state === 'output-error') {
        return (
            <KeyValuePairs
                items={[
                    {
                        label: 'Input',
                        value: (
                            <CodeView
                                content={input}
                                actions={
                                    <CopyToClipboard
                                        copyButtonAriaLabel="Copy input"
                                        copyErrorText="Failed to copy"
                                        copySuccessText="Copied"
                                        textToCopy={input}
                                    />
                                }
                            />
                        ),
                    },
                    {
                        label: 'Error message',
                        value: (
                            <CodeView
                                content={tool.errorText}
                                actions={
                                    <CopyToClipboard
                                        copyButtonAriaLabel="Copy error"
                                        copyErrorText="Failed to copy"
                                        copySuccessText="Copied"
                                        textToCopy={tool.errorText}
                                    />
                                }
                            />
                        ),
                    },
                ]}
            />
        );
    }

    const items: {label: string; value: React.ReactNode}[] = [
        {
            label: 'Input',
            value: (
                <CodeView
                    content={input}
                    actions={
                        <CopyToClipboard
                            copyButtonAriaLabel="Copy input"
                            copyErrorText="Failed to copy"
                            copySuccessText="Copied"
                            textToCopy={input}
                        />
                    }
                />
            ),
        },
    ];

    if (tool.output !== undefined && tool.output !== null) {
        const output = prettifyMaybeJson(tool.output);
        const lines = output.split('\n');
        const displayOutput = lines.length > 100 ? lines.slice(0, 100).join('\n') + '\n...' : output;
        items.push({
            label: 'Output',
            value: (
                <CodeView
                    content={displayOutput}
                    actions={
                        <CopyToClipboard
                            copyButtonAriaLabel="Copy output"
                            copyErrorText="Failed to copy"
                            copySuccessText="Copied"
                            textToCopy={output}
                        />
                    }
                />
            ),
        });
    }

    return <KeyValuePairs items={items} />;
}

function ToolStep({tool, childTools}: {tool: ToolUIPart; childTools: ToolUIPart[]}) {
    const [userExpanded, setUserExpanded] = useState<boolean | null>(null);
    const toolInProgress = tool.state === 'input-streaming' || tool.state === 'input-available';
    const allSucceeded = tool.state === 'output-available' && childTools.every((c) => c.state === 'output-available');
    const expanded = userExpanded ?? !allSucceeded;

    return (
        <div>
            <div style={{display: 'flex', alignItems: 'center', gap: 8}}>
                <StatusIndicator type={getStatusType(tool.state)}>
                    <Popover fixedWidth size="large" content={<ToolDetail tool={tool} />}>
                        {getStatusText(tool)}
                    </Popover>
                </StatusIndicator>
                {!toolInProgress && childTools.length > 0 && (
                    <span
                        role="button"
                        tabIndex={0}
                        style={{cursor: 'pointer', fontSize: 12, color: '#0972d3'}}
                        onClick={() => setUserExpanded(!expanded)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') setUserExpanded(!expanded);
                        }}
                    >
                        {expanded ? 'Hide steps' : `Show ${childTools.length} steps`}
                    </span>
                )}
            </div>
            {(toolInProgress || expanded) && childTools.length > 0 && (
                <div style={{marginLeft: 16, marginTop: 4, display: 'flex', flexDirection: 'column', gap: 2}}>
                    {childTools.map((child) => (
                        <StatusIndicator key={child.toolCallId} type={getStatusType(child.state)}>
                            <Popover fixedWidth size="large" content={<ToolDetail tool={child} />}>
                                {getStatusText(child)}
                            </Popover>
                        </StatusIndicator>
                    ))}
                </div>
            )}
        </div>
    );
}

interface ChatMessageProps extends ArtifactContext {
    message: UIMessage;
    isStreaming: boolean;
    userAlias?: string;
    onQuestionAnswer?: (params: {toolCallId: string; answer: string}) => void;
    renderArtifactsInline?: boolean;
}

function getPendingQuestions(message: UIMessage): {toolCallId: string; question: string; context?: string}[] {
    if (message.role !== 'assistant') return [];
    return message.parts
        .filter((p) => isToolUIPart(p) && getToolName(p as ToolUIPart) === ASK_QUESTION_TOOL && p.state === 'input-available')
        .map((p) => {
            const tool = p as ToolUIPart;
            const input = tool.input as {question: string; context?: string};
            return {toolCallId: tool.toolCallId, question: input.question, context: input.context};
        });
}

function QuestionDialog({
    question,
    onSubmit,
}: {
    question: {question: string; context?: string};
    onSubmit: (answer: string) => void;
}) {
    const [answer, setAnswer] = useState('');
    const [submitted, setSubmitted] = useState(false);
    const handleSubmit = () => {
        if (answer.trim() && !submitted) {
            setSubmitted(true);
            onSubmit(answer.trim());
        }
    };
    return (
        <div
            style={{
                backgroundColor: awsui.colorBackgroundDialog,
                border: `1px solid ${awsui.colorBorderStatusInfo}`,
                borderRadius: awsui.borderRadiusContainer,
                padding: awsui.spaceScaledM,
                marginTop: awsui.spaceScaledS,
            }}
        >
            <SpaceBetween size="s">
                <SpaceBetween size="xxs">
                    <Box variant="h4" color="text-status-info">
                        Question
                    </Box>
                    <div className="cassandra-streamdown-scope">
                        <Streamdown>{question.question}</Streamdown>
                    </div>
                    {question.context && (
                        <Box color="text-body-secondary" fontSize="body-s">
                            {question.context}
                        </Box>
                    )}
                </SpaceBetween>
                <Textarea
                    value={answer}
                    onChange={({detail}) => setAnswer(detail.value)}
                    placeholder="Type your answer..."
                    rows={2}
                />
                <Box float="right">
                    <Button variant="primary" onClick={handleSubmit} disabled={!answer.trim() || submitted}>
                        Submit
                    </Button>
                </Box>
            </SpaceBetween>
        </div>
    );
}

export default function ChatMessage({
    message,
    isStreaming,
    userAlias,
    onQuestionAnswer,
    renderArtifactsInline = false,
    chatId,
    credentials,
    onArtifactClick,
}: ChatMessageProps) {
    const isUser = message.role === 'user';

    const toolParentMap = new Map<string, string>();
    for (const part of message.parts) {
        const p = part as unknown as CustomDataPart;
        if (p.type === 'data-tool-parent') {
            toolParentMap.set(p.data.toolCallId, p.data.parentToolCallId);
        }
    }

    const getToolParent = (tool: ToolUIPart): string | undefined => {
        if (tool.state !== 'input-streaming') {
            const meta = (tool as any).callProviderMetadata?.frida?.parentToolCallId;
            if (typeof meta === 'string') return meta;
        }
        return toolParentMap.get(tool.toolCallId);
    };

    const avatar = isUser ? (
        <Avatar ariaLabel="User" tooltipText="User" initials={userAlias?.slice(0, 2).toUpperCase()} />
    ) : (
        <Avatar
            color="gen-ai"
            iconName="gen-ai"
            ariaLabel="Assistant"
            tooltipText="Cassandra"
            loading={isStreaming && !message.parts.some((p) => p.type === 'text' && (p as {text?: string}).text)}
        />
    );

    if (isUser) {
        const text = message.parts
            .filter((p) => p.type === 'text')
            .map((p) => (p as {text?: string}).text ?? '')
            .join('');
        return (
            <ChatBubble ariaLabel="User message" type="outgoing" avatar={avatar}>
                <div className="cassandra-streamdown-scope">
                    <Streamdown>{text}</Streamdown>
                </div>
            </ChatBubble>
        );
    }

    const content: React.ReactNode[] = [];

    message.parts.forEach((part, index) => {
        if (part.type === 'text') {
            const text = (part as {text?: string}).text ?? '';
            if (text) {
                content.push(
                    <div key={`text-${index}`} className="cassandra-streamdown-scope">
                        <Streamdown animated isAnimating={isStreaming}>
                            {text}
                        </Streamdown>
                    </div>
                );
            }
        } else if (part.type === 'reasoning') {
            const {reasoning, text: reasoningText} = part as {reasoning?: string; text?: string};
            content.push(
                <ExpandableSection key={`reasoning-${index}`} headerText="Reasoning" variant="footer" defaultExpanded={false}>
                    <Box variant="small">{reasoning ?? reasoningText ?? ''}</Box>
                </ExpandableSection>
            );
        } else if (isToolUIPart(part)) {
            const tool = part as ToolUIPart;
            if (getToolName(tool) === ASK_QUESTION_TOOL) return;
            const parent = getToolParent(tool);
            if (!parent) {
                const children = message.parts.filter(
                    (p) => isToolUIPart(p) && getToolParent(p as ToolUIPart) === tool.toolCallId
                ) as ToolUIPart[];
                content.push(<ToolStep key={tool.toolCallId} tool={tool} childTools={children} />);
            }
        } else if (
            (part as unknown as CustomDataPart).type === 'data-artifact-previews' &&
            renderArtifactsInline &&
            chatId &&
            credentials
        ) {
            const artifacts = (part as unknown as {type: 'data-artifact-previews'; data: ArtifactPreviewData[]}).data;
            if (artifacts?.length > 0) {
                content.push(
                    <div key={`artifacts-${index}`} className="cassandra-inline-charts">
                        {artifacts.map((artifact) => (
                            <ArtifactPreview
                                key={artifact.artifactId}
                                artifactId={artifact.artifactId}
                                name={artifact.name}
                                mimeType={artifact.mimeType}
                                sizeBytes={artifact.sizeBytes}
                                chatId={chatId}
                                credentials={credentials}
                                renderInline
                                onClick={onArtifactClick}
                            />
                        ))}
                    </div>
                );
            }
        }
    });

    const hasContent = content.length > 0;
    const pendingQuestions = getPendingQuestions(message);

    return (
        <>
            <ChatBubble ariaLabel="Assistant message" type="incoming" avatar={avatar} showLoadingBar={isStreaming && !hasContent}>
                <div style={{display: 'flex', flexDirection: 'column', gap: 8}}>
                    {hasContent ? content : isStreaming ? <StatusIndicator type="loading">Thinking...</StatusIndicator> : null}
                </div>
            </ChatBubble>
            {onQuestionAnswer &&
                pendingQuestions.map((q) => (
                    <QuestionDialog
                        key={q.toolCallId}
                        question={q}
                        onSubmit={(answer) => onQuestionAnswer({toolCallId: q.toolCallId, answer})}
                    />
                ))}
        </>
    );
}
